/**
 * Animation state machine for sprite entities.
 * Manages frame cycling with per-state tick rates.
 */

import type { Sprite } from "../sprites/SpriteSheet.js";

export type AgentAnimState = "idle" | "running" | "waiting" | "error" | "terminated";

/** Ticks per frame for each animation state (at 30fps). */
const FRAME_RATES: Record<AgentAnimState, number> = {
  idle: 11,       // 4 frames * 11 ticks = ~1.5s cycle
  running: 4,     // 6 frames * 4 ticks = ~0.8s cycle
  waiting: 10,    // 6 frames * 10 ticks = ~2.0s cycle
  error: 5,       // 4 frames * 5 ticks = ~0.6s cycle
  terminated: 1,  // 1 frame, doesn't matter
};

export class AnimationController {
  private sprites: Record<AgentAnimState, Sprite>;
  private state: AgentAnimState = "idle";
  private frameIndex = 0;
  private tickCount = 0;

  constructor(sprites: Record<AgentAnimState, Sprite>) {
    this.sprites = sprites;
  }

  /** Transition to a new animation state. */
  transition(newState: AgentAnimState): void {
    if (this.state === newState) return;
    this.state = newState;
    this.frameIndex = 0;
    this.tickCount = 0;
  }

  /** Advance one tick (called at fixed timestep). */
  tick(): void {
    this.tickCount++;
    const rate = FRAME_RATES[this.state];
    if (this.tickCount >= rate) {
      this.tickCount = 0;
      const sprite = this.sprites[this.state];
      this.frameIndex = (this.frameIndex + 1) % sprite.frameCount;
    }
  }

  /** Get the current sprite and frame index for drawing. */
  current(): { sprite: Sprite; frameIndex: number } {
    return {
      sprite: this.sprites[this.state],
      frameIndex: this.frameIndex,
    };
  }

  /** Get the current animation state. */
  getState(): AgentAnimState {
    return this.state;
  }
}
